// src/components/ChristmasGiftPromo.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gift,
  Sparkles,
  Clock,
  Check,
  ArrowRight,
  Heart,
  Star,
  Calendar,
} from "lucide-react";
import Lottie from "lottie-react";
import { NavbarDemo } from "./NavBar";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import jumpingGiftBoxesAnimation from "../images/Jumping gift boxes.json";

export default function ChristmasGiftPromo() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    // Check if promotion has expired
    const checkExpiration = () => {
      const now = new Date();
      const expiryDate = new Date("2025-12-31T23:59:59");

      if (now > expiryDate) {
        setIsExpired(true);
        // Redirect to regular gift cards page after 3 seconds
        setTimeout(() => {
          navigate("/gift-cards");
        }, 3000);
        return;
      }

      // Calculate time left
      const diff = expiryDate - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };

    checkExpiration();
    const interval = setInterval(checkExpiration, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [navigate]);

  const giftOptions = [
    {
      id: "standard",
      amount: 100,
      tier: "Standard",
      originalPrice: 100,
      discountedPrice: 85,
      savings: 15,
      popular: true,
      features: [
        "60-minute relaxation massage",
        "Full body treatment",
        "Professional therapist",
        "Valid for 1 year",
      ],
    },
    {
      id: "premium",
      amount: 200,
      tier: "Premium",
      originalPrice: 200,
      discountedPrice: 170,
      savings: 30,
      features: [
        "Two 60-minute sessions",
        "Choice of massage type",
        "Premium therapists",
        "Valid for 1 year",
        "Flexible scheduling",
      ],
    },
  ];

  if (isExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            🎄 Promotion Ended
          </h1>
          <p className="text-gray-600">
            Redirecting you to our gift cards page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50">
      <NavbarDemo />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-red-600 text-white">
        {/* Animated background pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-700/30 to-red-700/30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        {/* Lottie Animation Container */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-10 right-10 w-96 h-96 opacity-80 hidden lg:block">
            <Lottie
              animationData={jumpingGiftBoxesAnimation}
              loop={true}
              autoplay={true}
              className="w-full h-full"
            />
          </div>
          <div className="absolute bottom-10 left-10 w-64 h-64 opacity-60 hidden md:block">
            <Lottie
              animationData={jumpingGiftBoxesAnimation}
              loop={true}
              autoplay={true}
              className="w-full h-full"
              style={{ transform: "scaleX(-1)" }}
            />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2.5 mb-6 shadow-lg">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-semibold">
                  Limited Time: Ends {timeLeft}
                </span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
                <span className="block mb-2">🎄 Holiday Gift</span>
                <span className="block bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">
                  Special
                </span>
              </h1>

              <p className="text-2xl md:text-3xl font-bold mb-4 text-yellow-100">
                Save 15% on Gift Cards $100+
              </p>

              <p className="text-lg md:text-xl text-white/95 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                Give the gift of relaxation this holiday season. Perfect for
                friends, family, or treating yourself!
              </p>

              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md rounded-lg px-4 py-2 mb-8">
                <span className="text-sm font-medium">Use code:</span>
                <span className="font-bold text-lg bg-white text-green-600 px-4 py-1.5 rounded">
                  HOLIDAYS
                </span>
                <span className="text-sm">at checkout</span>
              </div>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => navigate("/gift-cards")}
                  size="lg"
                  className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <Gift className="w-5 h-5 mr-2" />
                  Shop Gift Cards
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white hover:bg-white/10 text-lg px-8 py-6 h-auto backdrop-blur-sm hover:scale-105 transition-all duration-300">
                  Explore Services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Lottie Animation - Mobile/Center */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="relative flex items-center justify-center lg:hidden">
              <div className="w-full max-w-md">
                <Lottie
                  animationData={jumpingGiftBoxesAnimation}
                  loop={true}
                  autoplay={true}
                  className="w-full h-full"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Snowflake decorations */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-white/40 text-xl md:text-2xl"
              initial={{
                top: `-${Math.random() * 100}px`,
                left: `${Math.random() * 100}%`,
                opacity: 0,
              }}
              animate={{
                top: "110%",
                rotate: 360,
                opacity: [0, 1, 1, 0],
              }}
              transition={{
                duration: 12 + Math.random() * 8,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear",
              }}>
              ❄️
            </motion.div>
          ))}
        </div>
      </div>

      {/* Featured Gift Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Limited Time Offers
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto">
            Perfect gifts for your loved ones this holiday season
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {giftOptions.map((option, index) => (
            <motion.div
              key={option.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              whileHover={{ y: -5 }}
              className="h-full">
              <Card
                className={`relative overflow-hidden h-full hover:shadow-2xl transition-all duration-300 group ${
                  option.popular
                    ? "border-2 border-green-500 shadow-lg"
                    : "border border-gray-200"
                }`}>
                {option.popular && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 text-sm font-semibold rounded-bl-xl shadow-lg">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    Most Popular
                  </motion.div>
                )}

                <CardContent className="p-8 md:p-10">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {option.tier} Package
                    </h3>
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <span className="text-3xl md:text-4xl font-bold text-gray-400 line-through">
                        ${option.originalPrice}
                      </span>
                      <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        ${option.discountedPrice}
                      </span>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="inline-block bg-gradient-to-r from-red-100 to-pink-100 text-red-600 px-5 py-2 rounded-full font-bold text-sm md:text-base shadow-md">
                      Save ${option.savings}!
                    </motion.div>
                  </div>

                  <div className="space-y-4 mb-8">
                    {option.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.15 + idx * 0.05 }}
                        className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <span className="text-gray-700 text-base">
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  <Button
                    onClick={() =>
                      navigate("/gift-cards", {
                        state: { preselectedAmount: option.amount },
                      })
                    }
                    className="w-full bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white text-lg py-6 h-auto shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                    <Gift className="w-5 h-5 mr-2" />
                    Get This Gift Card
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Also available */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center">
          <p className="text-gray-600 mb-6 text-lg">
            Looking for a different amount?
          </p>
          <Button
            onClick={() => navigate("/gift-cards")}
            variant="outline"
            size="lg"
            className="border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 text-lg px-8 py-6 h-auto transition-all duration-300">
            View All Gift Card Options
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>

      {/* How it Works */}
      <div className="bg-gradient-to-b from-white to-gray-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                step: 1,
                title: "Choose Your Amount",
                description:
                  "Select from $100 or $200 gift cards (or customize your own)",
                icon: Gift,
              },
              {
                step: 2,
                title: "Use Code HOLIDAYS",
                description:
                  "Apply code at checkout to get 15% off automatically",
                icon: Sparkles,
              },
              {
                step: 3,
                title: "Send & Enjoy",
                description:
                  "Schedule delivery and let your loved ones enjoy a relaxing massage",
                icon: Heart,
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ y: -5 }}
                className="text-center group">
                <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-red-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Explore More Section */}
      <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-red-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore More
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover our range of services and book your perfect massage
              experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: "Book a Session",
                description: "Schedule your relaxing massage appointment today",
                icon: Calendar,
                action: "Book Now",
                navigate: "/",
                color: "from-green-600 to-emerald-600",
              },
              {
                title: "Browse Services",
                description:
                  "Explore our range of therapeutic massage services",
                icon: Star,
                action: "View Services",
                navigate: "/",
                color: "from-emerald-600 to-teal-600",
              },
              {
                title: "Learn More",
                description: "Find out more about our therapists and approach",
                icon: Heart,
                action: "Discover",
                navigate: "/",
                color: "from-red-600 to-pink-600",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group">
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200 cursor-pointer">
                  <CardContent className="p-6 md:p-8 text-center">
                    <div
                      className={`w-16 h-16 bg-gradient-to-br ${item.color} text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm md:text-base">
                      {item.description}
                    </p>
                    <Button
                      onClick={() => navigate(item.navigate)}
                      variant="outline"
                      className="w-full border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 group-hover:scale-105 transition-all duration-300">
                      {item.action}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Footer */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-600 via-emerald-600 to-red-600 text-white py-16 md:py-20">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>
            <motion.h2
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6">
              Don't Miss Out!
              <br />
              <span className="text-yellow-200">Offer Ends December 31st</span>
            </motion.h2>
            <p className="text-xl md:text-2xl mb-8 text-white/95 max-w-2xl mx-auto">
              Give the perfect gift this holiday season and make someone's day
              special
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => navigate("/gift-cards")}
                  size="lg"
                  className="bg-white text-green-600 hover:bg-gray-100 text-lg px-10 py-7 h-auto shadow-2xl hover:shadow-3xl transition-all duration-300">
                  <Gift className="w-6 h-6 mr-2" />
                  Start Shopping Now
                  <ArrowRight className="w-6 h-6 ml-2" />
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => navigate("/")}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white hover:bg-white/10 text-lg px-10 py-7 h-auto backdrop-blur-sm transition-all duration-300">
                  Explore Services
                  <ArrowRight className="w-6 h-6 ml-2" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
